// src/pages/auth/RegisterLogin.jsx
import { useState, useEffect, useRef, useContext } from "react";
import { useNavigate, Link as RouterLink, Link } from "react-router-dom";
import PropTypes from "prop-types";
import {
  FaCheck,
  FaGoogle,
  FaFacebookF,
  FaMicrosoft,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import ReCAPTCHA from "react-google-recaptcha";
import { useGoogleLogin } from "@react-oauth/google";
import FacebookLogin from "react-facebook-login";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../config/msalConfig";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import "./registerlogin.css";
import LoadingOverlay from "../../components/LoadingOverlay";

// Función para capitalizar la primera letra
const capitalize = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Requisitos de contraseña
const getPasswordRequirements = (password) => {
  const requirements = [];
  if (password.length < 6) requirements.push("mínimo 6 caracteres");
  if (!/[A-Z]/.test(password)) requirements.push("1 mayúscula");
  if (!/[0-9]/.test(password)) requirements.push("1 número");
  if (!/[^a-zA-Z0-9]/.test(password))
    requirements.push("1 carácter especial (Ejemplo: @ #)");
  return requirements;
};

function RegisterLogin({ role = "user" }) {
  const navigate = useNavigate();
  const { updateUser } = useContext(AuthContext);

  // Determina si es "employer" o "user"
  const roleParam = role.trim().toLowerCase() || "user";
  const esEmpleador = roleParam === "employer";

  // Para Microsoft
  const { instance } = useMsal();

  // Refs para abortar requests y hacer timeouts
  const timerRef = useRef(null);
  const controllerRef = useRef(new AbortController());

  // Estados principales
  const [username, setUsername] = useState("");
  const [nombre, setNombre] = useState("");
  const [apellido, setApellido] = useState("");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [terminos, setTerminos] = useState(false);

  // Solo si es empleador
  const [nombreEmpresa, setNombreEmpresa] = useState("");
  const [puestoEmpresa, setPuestoEmpresa] = useState("");
  const [estado, setEstado] = useState("");
  const [codigoPostal, setCodigoPostal] = useState("");
  const [telefono, setTelefono] = useState("");
  const [idioma, setIdioma] = useState("es");

  // Errores y mensajes
  const [usernameError, setUsernameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [confirmEmailError, setConfirmEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPassError, setConfirmPassError] = useState("");
  const [serverError, setServerError] = useState("");

  // Mensajes de validación positivos
  const [emailStatus, setEmailStatus] = useState("");
  const [passwordStatus, setPasswordStatus] = useState("");

  // Para mostrar/ocultar contraseñas
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // reCAPTCHA y overlay de carga
  const [captchaToken, setCaptchaToken] = useState(null);
  const [loading, setLoading] = useState(false);

  const apiUrl = import.meta.env.VITE_STRAPI_URL || "http://localhost:1337";

  // (Opcional) Cargar países, etc.
  useEffect(() => {
    fetch("https://restcountries.com/v3.1/all")
      .then((res) => res.json())
      .then(() => {})
      .catch(console.error);
  }, []);

  // Cada vez que el usuario cambia "email" o "confirmEmail", validamos si coinciden
  useEffect(() => {
    if (confirmEmail && confirmEmail !== email) {
      setConfirmEmailError("Los correos no coinciden");
    } else {
      setConfirmEmailError("");
    }
  }, [email, confirmEmail]);

  /*****************************
   *        VALIDACIONES
   *****************************/

  // Usuario (username) - Real-time con setTimeout
  const handleUsernameChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9]/g, "").replace(/\s/g, "");
    setUsername(value);
    setUsernameError("");

    clearTimeout(timerRef.current);
    controllerRef.current.abort();
    controllerRef.current = new AbortController();
    const { signal } = controllerRef.current;

    // Si el usuario no está vacío, hacemos la verificación
    if (value.trim()) {
      timerRef.current = setTimeout(async () => {
        try {
          const response = await fetch(
            `${apiUrl}/api/users?filters[username][$eqi]=${encodeURIComponent(value)}`,
            { signal, headers: { "Content-Type": "application/json" } }
          );
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error?.message || `Error: ${response.status}`);
          }
          const array = await response.json();
          if (Array.isArray(array) && array.length > 0) {
            setUsernameError("No disponible");
          } else {
            setUsernameError("Disponible");
          }
        } catch (error) {
          if (error.name !== "AbortError") {
            console.error("Error en verificación username:", error);
            setUsernameError(
              error.message.includes("fetch")
                ? "Error de conexión"
                : "Error en verificación"
            );
          }
        }
      }, 500);
    }
  };

  // Email - solo guardamos en state y validamos con onBlur
  const handleEmailChange = (e) => {
    setEmail(e.target.value.trim().toLowerCase());
    setEmailError("");
    setEmailStatus("");
  };

  // Validación de email y verificación en la BD al perder el foco
  const handleEmailBlur = async () => {
    if (!email) return; // si está vacío, no hacemos nada

    // Verifica formato
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Este no es un correo válido");
      setEmailStatus("");
      return;
    }

    try {
      const response = await fetch(
        `${apiUrl}/api/users?filters[email][$eqi]=${encodeURIComponent(email)}&filters[userType][$eq]=${roleParam}`,
        { headers: { "Content-Type": "application/json" } }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || `Error: ${response.status}`);
      }
      const array = await response.json();
      if (Array.isArray(array) && array.length > 0) {
        setEmailError("Correo ya está en uso. Por favor, usa otro correo o ");
        setEmailStatus("");
      } else {
        setEmailError("");
        setEmailStatus("Disponible");
      }
    } catch (error) {
      console.error("Error en verificación email:", error);
      setEmailError(
        error.message.includes("fetch")
          ? "Error de conexión"
          : "Error en verificación"
      );
      setEmailStatus("");
    }
  };

  // Confirmar Correo
  const handleConfirmEmailChange = (e) => {
    const value = e.target.value.trim().toLowerCase();
    setConfirmEmail(value);
    setConfirmEmailError(value === email ? "" : "Los correos no coinciden");
  };

  // Password
  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    const missing = getPasswordRequirements(value);
    if (missing.length > 0) {
      setPasswordError(`Falta: ${missing.join(", ")}`);
      setPasswordStatus("");
    } else {
      setPasswordError("");
      setPasswordStatus("Contraseña bien elaborada");
    }
  };

  // Confirmar Password
  const handleConfirmPassChange = (e) => {
    const value = e.target.value;
    setConfirmPass(value);
    setConfirmPassError(value === password ? "" : "Las contraseñas no coinciden");
  };

  // Teléfono y código postal (solo dígitos)
  const handleTelefonoChange = (e) => {
    setTelefono(e.target.value.replace(/[^\d]/g, ""));
  };
  const handleCodigoPostalChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, "");
    if (value.length <= 6) setCodigoPostal(value);
  };

  /*****************************
   *    REGISTRO CON REDES
   *****************************/

  // Facebook
  const facebookResponse = async (response) => {
    setLoading(true);
    try {
      const { accessToken } = response;
      if (!accessToken) {
        setServerError("No se obtuvo el accessToken de Facebook");
        return;
      }
      const { data } = await axios.post(
        `${apiUrl}/api/facebook-custom?role=${roleParam}`,
        { accessToken }
      );
      localStorage.setItem("jwt", data.jwt);
      localStorage.setItem("user", JSON.stringify(data.user));
      updateUser(data.user);
      navigate(`/mi-cuenta?role=${roleParam}`);
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.error &&
        error.response.data.error.message
      ) {
        setServerError(error.response.data.error.message);
      } else {
        setServerError("Error en registro con Facebook");
      }
    } finally {
      setLoading(false);
    }
  };

  // Google
  const googleLogin = useGoogleLogin({
    onSuccess: async ({ code }) => {
      setLoading(true);
      try {
        const response = await axios.post(
          `${apiUrl}/api/google-custom?role=${roleParam}`,
          { code }
        );
        localStorage.setItem("jwt", response.data.jwt);
        localStorage.setItem("user", JSON.stringify(response.data.user));
        updateUser(response.data.user);
        navigate(`/mi-cuenta?role=${roleParam}`);
      } catch (error) {
        if (
          error.response &&
          error.response.data &&
          error.response.data.error &&
          error.response.data.error.message
        ) {
          setServerError(error.response.data.error.message);
        } else {
          setServerError("Error en registro con Google");
        }
      } finally {
        setLoading(false);
      }
    },
    flow: "auth-code",
  });
  const handleGoogleRegister = () => {
    googleLogin();
  };

  // Microsoft
  const handleMicrosoftMsalRegister = async () => {
    setLoading(true);
    try {
      const loginResponse = await instance.loginPopup(loginRequest);
      if (loginResponse?.idToken) {
        const response = await axios.post(
          `${apiUrl}/api/microsoft-custom?role=${roleParam}`,
          { token: loginResponse.idToken },
          { headers: { "Content-Type": "application/json" } }
        );
        localStorage.setItem("jwt", response.data.jwt);
        localStorage.setItem("user", JSON.stringify(response.data.user));
        updateUser(response.data.user);
        navigate(`/mi-cuenta?role=${roleParam}`);
      } else {
        setServerError("No se encontró idToken en la respuesta de MSAL");
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.error &&
        error.response.data.error.message
      ) {
        setServerError(error.response.data.error.message);
      } else {
        setServerError("Error al autenticar con Microsoft");
      }
    } finally {
      setLoading(false);
    }
  };
  const handleMicrosoftRegister = () => {
    handleMicrosoftMsalRegister();
  };

  /*****************************
   *      REGISTRO LOCAL
   *****************************/
  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    setLoading(true);

    // Si tenemos errores en validaciones en tiempo real:
    if (
      usernameError ||
      emailError ||
      confirmEmailError ||
      passwordError ||
      confirmPassError
    ) {
      setServerError("Debe llenar correctamente todos los campos requeridos");
      setLoading(false);
      return;
    }
    if (!captchaToken) {
      setLoading(false);
      alert("Por favor, verifica que eres humano.");
      return;
    }
    if (!username.trim()) {
      setLoading(false);
      alert("Usuario requerido");
      return;
    }
    if (!/^[a-z0-9]+$/.test(username)) {
      setLoading(false);
      alert("Solo letras minúsculas y números permitidos en usuario");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLoading(false);
      alert("Correo electrónico inválido");
      return;
    }
    if (email !== confirmEmail) {
      setLoading(false);
      alert("Correos no coinciden");
      return;
    }
    if (!/^(?=.*[A-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9]).{6,}$/.test(password)) {
      setLoading(false);
      alert("Contraseña débil");
      return;
    }
    if (password !== confirmPass) {
      setLoading(false);
      alert("Contraseñas diferentes");
      return;
    }
    if (esEmpleador && !/^\d{4,6}$/.test(codigoPostal)) {
      setLoading(false);
      alert("Código postal inválido");
      return;
    }
    if (!terminos) {
      setLoading(false);
      alert("Acepta los términos");
      return;
    }

    // Payload para el POST
    const payload = {
      username: username.trim().toLowerCase(),
      email: email.trim().toLowerCase(),
      password,
      nombre: capitalize(nombre.trim()),
      apellido: capitalize(apellido.trim()),
      userType: esEmpleador ? "employer" : "user",
      captchaToken,
      idioma,
      ...(esEmpleador && {
        nombreEmpresa,
        puestoEmpresa,
        estado,
        codigoPostal,
        telefono,
      }),
    };

    try {
      const res = await fetch(`${apiUrl}/api/auth/local/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (!res.ok) {
        const errorMessage = data.error?.message || "Error en el registro";
        if (errorMessage.toLowerCase().includes("username")) {
          setUsernameError("Usuario ya existe");
        }
        if (errorMessage.toLowerCase().includes("email")) {
          setEmailError("Correo ya está en uso. Por favor, usa otro correo o ");
        }
        setServerError(errorMessage);
        throw new Error(errorMessage);
      }

      if (data.jwt) {
        localStorage.setItem("jwt", data.jwt);
        localStorage.setItem("user", JSON.stringify(data.user));
        updateUser(data.user);
        alert("Registro exitoso (sin confirmación de correo).");
        navigate(`/mi-cuenta?role=${roleParam}`);
      } else {
        alert("Registro exitoso. Revisa tu correo para confirmar tu cuenta.");
        navigate("/email-confirmation");
      }
    } catch (error) {
      if (error.message.includes("Internal Server Error")) {
        setServerError("Error en el servidor. Contacta al soporte");
      } else if (!serverError) {
        setServerError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Mostrar/ocultar contraseña
  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const toggleShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div className="register-container">
      {loading && <LoadingOverlay message="Cargando..." color="#f5a623" />}
      <div className="register-card">
        <h2 className="register-title">
          FORMULARIO DE REGISTRO
          <br />
          <span className="role-label">
            {esEmpleador ? "EMPLEADOR" : "USUARIO"}
          </span>
        </h2>

        {/* Botones de registro con redes sociales */}
        <div className="social-register-section">
          <button type="button" className="btn btn-google" onClick={handleGoogleRegister}>
            <FaGoogle className="me-2" /> Regístrate con Google
          </button>
          <FacebookLogin
            appId="1155198232519715"
            autoLoad={false}
            fields="name,email,picture"
            callback={facebookResponse}
            disableMobileRedirect={true}
            redirectUri="https://api.workingunidos.com/api/facebook-custom"
            textButton="Regístrate con Facebook"
            cssClass="btn btn-facebook"
            icon={<FaFacebookF className="me-2" />}
          />
          <button type="button" className="btn btn-microsoft" onClick={handleMicrosoftRegister}>
            <FaMicrosoft className="me-2" /> Regístrate con Microsoft
          </button>
        </div>

        {/* Mensaje global de error */}
        {serverError && (
          <div className="alert alert-danger text-center mt-3">
            {serverError}
            {serverError.includes("Correo ya está en uso") && (
              <>
                {" "}
                <RouterLink to={`/login?role=${roleParam}`} className="ms-1">
                  Inicia sesión
                </RouterLink>
              </>
            )}
          </div>
        )}

        <div className="divider">
          <hr className="divider-line" />
        </div>

        {/* Formulario de registro local */}
        <form onSubmit={handleSubmit} className="register-form">
          {/* USUARIO */}
          <div className="form-group">
            <label>
              Usuario <span className="required">*</span>
            </label>
            <div className="input-wrapper">
              <input
                type="text"
                value={username}
                onChange={handleUsernameChange}
                required
                className="form-control"
                placeholder="Elige un usuario"
              />
              {usernameError && (
                <span
                  className={`validation-message ${
                    usernameError.toLowerCase().includes("no disponible")
                      ? "error"
                      : "success"
                  }`}
                >
                  {usernameError.toLowerCase().includes("no disponible") ? (
                    <>
                      {/* Sin ícono grande, solo texto */}
                      <span>{usernameError}</span>
                    </>
                  ) : (
                    <>
                      <FaCheck className="success-icon" /> <span>{usernameError}</span>
                    </>
                  )}
                </span>
              )}
            </div>
          </div>

          {/* NOMBRE */}
          <div className="form-group">
            <label>
              Nombre <span className="required">*</span>
            </label>
            <div className="input-wrapper">
              <input
                type="text"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                onBlur={(e) => setNombre(capitalize(e.target.value))}
                required
                className="form-control"
                placeholder="Escribe tu nombre"
              />
            </div>
          </div>

          {/* APELLIDO */}
          <div className="form-group">
            <label>
              Apellido <span className="required">*</span>
            </label>
            <div className="input-wrapper">
              <input
                type="text"
                value={apellido}
                onChange={(e) => setApellido(e.target.value)}
                onBlur={(e) => setApellido(capitalize(e.target.value))}
                required
                className="form-control"
                placeholder="Escribe tu apellido"
              />
            </div>
          </div>

          {/* CORREO ELECTRÓNICO */}
          <div className="form-group">
            <label>
              Correo Electrónico <span className="required">*</span>
            </label>
            <div className="input-wrapper">
              <input
                type="email"
                value={email}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
                required
                className="form-control"
                placeholder="Introduce tu Correo Electrónico"
              />
              {emailError && (
                <span className="validation-message error">
                  {/* Se quita el ícono grande, solo texto */}
                  <span>{emailError}</span>
                </span>
              )}
              {!emailError && email && emailStatus && (
                <span className="validation-message success">
                  <FaCheck className="success-icon" /> <span>{emailStatus}</span>
                </span>
              )}
            </div>
          </div>

          {/* CONFIRMAR CORREO */}
          <div className="form-group">
            <label>
              Confirmar Correo <span className="required">*</span>
            </label>
            <div className="input-wrapper">
              <input
                type="email"
                value={confirmEmail}
                onChange={handleConfirmEmailChange}
                required
                className="form-control"
                placeholder="Confirma tu correo"
              />
              {confirmEmailError && (
                <span className="validation-message error">
                  <span>{confirmEmailError}</span>
                </span>
              )}
            </div>
          </div>

          {/* CONTRASEÑA */}
          <div className="form-group">
            <label>
              Contraseña <span className="required">*</span>
            </label>
            <div className="input-wrapper password-wrapper" style={{ position: "relative" }}>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={handlePasswordChange}
                required
                className="form-control"
                placeholder="Elige una contraseña"
              />
              <span
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: "absolute",
                  right: "1rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  color: "#555",
                }}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
            <p className="help-text">
              <strong>Requisitos:</strong> 6+ caracteres, 1 mayúscula, 1 número, 1 carácter especial (Ejemplo: @ #)
            </p>
            {password && (
              passwordStatus ? (
                <span className="validation-message success">
                  <FaCheck className="success-icon" /> <span>{passwordStatus}</span>
                </span>
              ) : (
                passwordError && (
                  <span className="validation-message error">
                    <span>{passwordError}</span>
                  </span>
                )
              )
            )}
          </div>

          {/* CONFIRMAR CONTRASEÑA */}
          <div className="form-group confirm-group">
            <label>
              Confirmar Contraseña <span className="required">*</span>
            </label>
            <div className="input-wrapper password-wrapper" style={{ position: "relative" }}>
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPass}
                onChange={handleConfirmPassChange}
                required
                className="form-control"
                placeholder="Confirma tu contraseña"
              />
              <span
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: "absolute",
                  right: "1rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  color: "#555",
                }}
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
              {confirmPassError && (
                <span className="validation-message error">
                  <span>{confirmPassError}</span>
                </span>
              )}
            </div>
          </div>

          {/* RECAPTCHA */}
          <div className="form-group">
            <ReCAPTCHA
              sitekey="6LfwdukqAAAAAKyiQumkjFhZh_i4yIS22P0_OTOa"
              onChange={(token) => setCaptchaToken(token)}
              onExpired={() => setCaptchaToken(null)}
            />
          </div>

          {/* TÉRMINOS Y CONDICIONES */}
          <div className="form-group terms-group">
            <p className="terms-text">
              Acepto los <Link to="/terminos">términos</Link> y la{" "}
              <Link to="/politicas-de-privacidad">política de privacidad</Link>{" "}
              <span className="required">*</span>
            </p>
            <div className="terms-options">
              <label>
                <input
                  type="radio"
                  checked={terminos}
                  onChange={() => setTerminos(true)}
                  required
                />{" "}
                Sí
              </label>
              <label>
                <input
                  type="radio"
                  checked={!terminos}
                  onChange={() => setTerminos(false)}
                  required
                />{" "}
                No
              </label>
            </div>
          </div>

          <button type="submit" className="submit-button">
            REGÍSTRATE
          </button>

          {serverError && <p className="error-message">{serverError}</p>}
        </form>
      </div>
    </div>
  );
}

RegisterLogin.propTypes = {
  role: PropTypes.string,
};

export default RegisterLogin;